import React from 'react';
import { useRouter } from 'next/router';

import { Page } from '@tager/web-components';
import { FETCH_STATUSES, Nullable } from '@tager/web-core';

import ErrorPage from '@/pages/_error';
import NotFoundPage from '@/pages/404';
import { CustomApp_PageContext } from '@/typings/hocs';
import Layout from '@/components/Layout';
import {
  convertErrorToProps,
  getAliasString,
  getCurrentPath,
} from '@/utils/common';
import { getSharedThunkList } from '@/utils/thunks';
import {
  getProductByAliasThunk,
  getProductPostListByIdThunk,
  getProductRelationListByIdThunk,
  selectProductResource,
} from '@/store/reducers/pages/product/product';
import { FullProductType } from '@/typings/model';
import Product from '@/modules/Product';

type Props =
  | {
      pageType: 'PRODUCT_PAGE';
      product: Nullable<FullProductType>;
    }
  | {
      pageType: 'NOT_FOUND';
    }
  | {
      pageType: 'ERROR';
      error: Error;
    };

function ProductPage(props: Props) {
  const router = useRouter();
  const currentPath = getCurrentPath(router);

  const canonicalUrl = process.env.NEXT_PUBLIC_ORIGIN + currentPath;

  if (props.pageType === 'NOT_FOUND') {
    return <NotFoundPage />;
  }

  if (props.pageType === 'ERROR') {
    return <ErrorPage {...convertErrorToProps(props.error)} />;
  }

  return (
    <Page
      title={props.product?.pageTitle}
      description={props.product?.pageDescription}
      openGraphImage={props.product?.openGraphImage}
      canonicalUrl={canonicalUrl}
    >
      <Layout>
        <Product />
      </Layout>
    </Page>
  );
}

ProductPage.getInitialProps = async ({
  store,
  query,
  res,
}: CustomApp_PageContext): Promise<Props> => {
  const alias = getAliasString(query.alias);

  try {
    await Promise.all([
      ...getSharedThunkList(store.dispatch),
      store.dispatch(getProductByAliasThunk(alias)).then((product) => {
        if (product?.id) {
          return Promise.all([
            store.dispatch(getProductPostListByIdThunk(product.id)),
            store.dispatch(getProductRelationListByIdThunk(product.id)),
          ]);
        }
      }),
    ]);

    const productResource = selectProductResource(store.getState(), alias);

    if (productResource.status === FETCH_STATUSES.FAILURE) {
      if (res) {
        res.statusCode = 404;
      }
      return {
        pageType: 'NOT_FOUND',
      };
    }

    return {
      pageType: 'PRODUCT_PAGE',
      product: productResource.data,
    };
  } catch (error) {
    return {
      pageType: 'ERROR',
      error,
    };
  }
};

export default ProductPage;
